import { openDatabase } from '@/core/persistence/db'
import {
  PersistenceError,
  SharedInboxReadError,
  SharedInboxWriteError,
} from '@/core/persistence/errors'
import { StoreName, type SharedInboxRecord } from '@/core/persistence/types'

function requestResult<T>(request: IDBRequest<T>, write: boolean): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(write
      ? new SharedInboxWriteError(request.error)
      : new SharedInboxReadError(undefined, request.error))
  })
}

function transactionComplete(transaction: IDBTransaction, write: boolean): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onabort = () => reject(write
      ? new SharedInboxWriteError(transaction.error)
      : new SharedInboxReadError(undefined, transaction.error))
    transaction.onerror = () => reject(write
      ? new SharedInboxWriteError(transaction.error)
      : new SharedInboxReadError(undefined, transaction.error))
  })
}

interface ParsedSharedInboxRecord extends Omit<SharedInboxRecord, 'bytes'> {
  bytes: ArrayBuffer | Blob
}

function parseSharedInboxRecord(value: unknown): ParsedSharedInboxRecord {
  if (typeof value !== 'object' || value === null) {
    throw new SharedInboxReadError('Shared Inbox contains an invalid record.')
  }
  const record = value as Partial<SharedInboxRecord> & { file?: unknown }
  if (typeof record.id !== 'string' || !record.id.trim()) {
    throw new SharedInboxReadError('Shared Inbox contains a record without an id.')
  }
  const legacyBlob = record.file instanceof Blob ? record.file : null
  const legacyFile = typeof File !== 'undefined' && record.file instanceof File ? record.file : null
  const bytes = record.bytes instanceof ArrayBuffer ? record.bytes : legacyBlob
  if (!bytes) {
    throw new SharedInboxReadError(`Shared Inbox record "${record.id}" does not contain image bytes.`)
  }
  const receivedAt = record.receivedAt
  if (typeof receivedAt !== 'number' || !Number.isInteger(receivedAt) || receivedAt <= 0) {
    throw new SharedInboxReadError(`Shared Inbox record "${record.id}" has an invalid received time.`)
  }
  const fileName = record.fileName ?? legacyFile?.name
  const mimeType = record.mimeType ?? legacyFile?.type
  if (typeof fileName !== 'string' || !fileName.trim()) {
    throw new SharedInboxReadError(`Shared Inbox record "${record.id}" does not contain a file name.`)
  }
  if (typeof mimeType !== 'string') {
    throw new SharedInboxReadError(`Shared Inbox record "${record.id}" does not contain a MIME type.`)
  }
  return {
    id: record.id,
    bytes,
    fileName,
    mimeType,
    receivedAt,
  }
}

function sharedInboxId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  if (typeof crypto.getRandomValues !== 'function') {
    throw new SharedInboxWriteError(new Error('Shared Inbox requires cryptographic random values'))
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  return `share-${[...bytes].map((value) => value.toString(16).padStart(2, '0')).join('')}`
}

export async function putSharedInboxFiles(files: readonly File[]): Promise<void> {
  if (files.length === 0) throw new SharedInboxWriteError(new Error('Shared Inbox requires at least one file'))
  if (files.some((file) => !(file instanceof File))) {
    throw new SharedInboxWriteError(new Error('Shared Inbox accepts File values only'))
  }
  let fileContents: Array<{ file: File; bytes: ArrayBuffer }>
  try {
    fileContents = await Promise.all(files.map(async (file) => ({
      file,
      bytes: await file.arrayBuffer(),
    })))
  } catch (error) {
    throw new SharedInboxWriteError(error)
  }
  const database = await openDatabase()
  try {
    const transaction = database.transaction(StoreName.SharedInbox, 'readwrite')
    const store = transaction.objectStore(StoreName.SharedInbox)
    const receivedAt = Date.now()
    await Promise.all(fileContents.map(({ file, bytes }, index) => requestResult(store.add({
      id: sharedInboxId(),
      bytes,
      fileName: file.name,
      mimeType: file.type,
      receivedAt: receivedAt + index,
    } satisfies SharedInboxRecord), true)))
    await transactionComplete(transaction, true)
  } catch (error) {
    if (error instanceof PersistenceError) throw error
    throw new SharedInboxWriteError(error)
  } finally {
    database.close()
  }
}

export async function takeSharedInboxFiles(): Promise<File[]> {
  const database = await openDatabase()
  try {
    const transaction = database.transaction(StoreName.SharedInbox, 'readwrite')
    const store = transaction.objectStore(StoreName.SharedInbox)
    const values = await requestResult(store.getAll() as IDBRequest<unknown[]>, false)
    let records: ParsedSharedInboxRecord[]
    try {
      records = values.map(parseSharedInboxRecord)
        .sort((first, second) => first.receivedAt - second.receivedAt)
    } catch (error) {
      await requestResult(store.clear(), true)
      await transactionComplete(transaction, true)
      throw error
    }
    await requestResult(store.clear(), true)
    await transactionComplete(transaction, true)
    return records.map((record) => new File(
      [record.bytes],
      record.fileName,
      { type: record.mimeType },
    ))
  } catch (error) {
    if (error instanceof PersistenceError) throw error
    throw new SharedInboxReadError(undefined, error)
  } finally {
    database.close()
  }
}
